import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { View } from '../../primitives/View';
import { DARK_LIFTED } from '../../styles/elevation';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { mixHex, tintHex } from '../../utils/color';
import { CardImage } from './CardImage';
import { CardTagLabel } from './CardTagLabel';
import { FolderPreviewShape, FolderShape } from './FolderShape';

/** One item's preview inside the folder stack — a deck page or a subfolder. */
export interface FolderPreview {
  /** The deck's cover image (document/PDF preview). Falls back to `emoji`. */
  image?: string;
  /**
   * The deck's emoji, shown on the fallback page when there is no image (or
   * it fails to load). A deck with neither image nor emoji gets the default
   * 📝 page.
   */
  emoji?: string;
  /**
   * The deck's identity colour (hex) — tints the no-image emoji page's
   * background/border (same white-mix treatment as DeckCard's emoji tile).
   * Without it the page stays the plain white sheet.
   */
  color?: string;
  /**
   * When set, this preview is a SUBFOLDER, not a deck: rendered as a mini
   * folder shape (top-left tab, like the card's own folder back) filled with
   * the subfolder's identity colour. `image`/`emoji` are ignored.
   */
  folderColor?: string;
}

/** Fallback page emoji for a deck with no cover image and no emoji. */
export const DEFAULT_PREVIEW_EMOJI = '📝';

// Preview lift
const PAGE_LIFT_HOVER = [
  'web:group-hover/card:-translate-y-[4px]',
  'web:group-hover/card:-translate-y-[12px]',
  'web:group-hover/card:-translate-y-[20px]',
];
const PAGE_LIFT_FORCED = [
  '-translate-y-[4px]',
  '-translate-y-[12px]',
  '-translate-y-[20px]',
];
// Unprefixed on purpose: NativeWind drives native transitions through
// reanimated, so the press-lift eases exactly like the web hover. Static from
// the first render, so the interop "animated" upgrade happens at mount.
const PAGE_LIFT_TRANSITION = 'transition-transform duration-200 ease-out';

// Per-count stack geometry (back → front), per grid variant. Each page is
// centred (symmetric `inset-x`) and pinned to the tile bottom; narrower +
// higher pages sit behind, so their top edges peek in a staircase — the
// Figma folder stack. The `insetX` percentages encode the EXACT Figma page
// widths over the card width (percentages, so they hold under the tablet
// responsive scale and the expanded card's max-w shrink):
//   compact  (180): front 130, middle 110, back 94
//   expanded (270): front 202, middle 183, back 147 (5656:17084 — the
//            revised "Folders Updated" folder card, shared by the revised
//            study-group card 5729:10426)
// The sets are NOT one ratio apart, hence the per-variant tables. The `top`
// staircase values are the Figma resting page tops as percentages of the
// tile height — the FULL card on both sizes now that the pages extend
// under the panel to the card bottom (expanded: the Figma tops 44 / 49.6 /
// 56.2 over the old 195 region, re-based onto the 224 card = ×195/224;
// compact: 3.81px/180 = 2.12% steps).
export const FOLDER_PAGE_LAYOUTS: Record<
  'compact' | 'expanded',
  Record<number, { insetX: number; top: number }[]>
> = {
  compact: {
    1: [{ insetX: 13.89, top: 30 }],
    2: [
      { insetX: 19.44, top: 28.88 },
      { insetX: 13.89, top: 31 },
    ],
    3: [
      { insetX: 23.89, top: 26.77 },
      { insetX: 19.44, top: 28.88 },
      { insetX: 13.89, top: 31 },
    ],
  },
  expanded: {
    1: [{ insetX: 12.59, top: 24.29 }],
    2: [
      { insetX: 16.11, top: 22.15 },
      { insetX: 12.59, top: 25.1 },
    ],
    3: [
      { insetX: 22.78, top: 19.64 },
      { insetX: 16.11, top: 22.15 },
      { insetX: 12.59, top: 25.1 },
    ],
  },
};

// Depth-graded dim over the pages BEHIND the front one — the Figma stack
// (3374:8783 / 3374:10048): front 0%, one back 5%, two back 10% black. The
// gradient (not a flat dim) is what makes the stack read as receding depth.
// Light only: in dark a black dim sinks the back pages below the lifted step
// toward the surface — half steps reading as holes; their offset rims carry
// the stack there (styles/elevation).
const PAGE_DIM = [
  '',
  'bg-black/5 dark:bg-transparent',
  'bg-black/10 dark:bg-transparent',
];
/** The same gradient as a black mix — for the subfolder shape's flat fill. */
const PAGE_DIM_MIX = [0, 0.05, 0.1];

// Native side of the page shadow (`web:shadow-card-page`, Figma 3374:9911 —
// 0/1.169 blur 4.674 @ 10%): NativeWind can't compile box-shadow, so the RN
// shadow props mirror it (radius = blur/2, the CARD_NATIVE_SHADOW rule).
const PAGE_NATIVE_SHADOW = {
  shadowColor: '#000000',
  shadowOffset: { width: 0, height: 1 },
  shadowOpacity: 0.1,
  shadowRadius: 2.3,
  // No `elevation` — same reason as FOLDER_PREVIEW_NATIVE_SHADOW below:
  // Android's rectangular elevation shadow halos the folder shape's
  // transparent tab notch instead of following it.
} as const;

// Native side of the subfolder preview's shadow — the `card-cover` recipe
// (0/2 blur 7 @ 10%) the A4 deck thumbs use, NOT the Figma exports' baked
// 6% Shadow-Secondary: at 6% the web drop-shadow read as no shadow at all,
// and the previews should carry the same weight as the deck pages beside
// them. iOS derives the layer shadow from the wrapper's composited alpha,
// so it follows the folder shape's tab notch exactly. No `elevation`:
// Android's rectangular elevation shadow would halo the transparent notch
// — Android degrades shadowless, the DS convention.
export const FOLDER_PREVIEW_NATIVE_SHADOW = {
  shadowColor: '#000000',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.1,
  shadowRadius: 3.5,
} as const;
/** Web twin of {@link FOLDER_PREVIEW_NATIVE_SHADOW} — `shadow-card-cover`
 *  as a drop-shadow FILTER so it hugs the tab notch (a box-shadow would
 *  halo the transparent corner). */
export const FOLDER_PREVIEW_WEB_SHADOW = 'drop-shadow(0 2px 7px rgba(0,0,0,0.1))';

/** One stacked page — its own media→fallback state (Avatar pattern). */
function PreviewPage({
  preview,
  insetX,
  top,
  liftDepth,
  lifted,
  size,
}: {
  preview: FolderPreview;
  insetX: number;
  top: number;
  /** Depth from the FRONT of the stack (0 = front) — lift + dim + shadow. */
  liftDepth: number;
  /** Force the hover lift (drop-target highlight). */
  lifted: boolean;
  /** Grid subvariant — picks the subfolder preview shape's export. */
  size: 'compact' | 'expanded';
}) {
  const [failed, setFailed] = useState(false);
  const scale = useResponsiveScale();
  const { isDark } = useTheme();
  const showImage = !!preview.image && !failed;
  const handleFailed = useCallback(() => setFailed(true), []);
  // The page's CONTENT, memoized away from the lift. `lifted` flips on every
  // press of the card this stack sits in and it only drives the wrapper's
  // translate class — but the content is a `CardTagLabel`, i.e. a
  // `MarqueeLabel` with a measuring copy and a gradient mask, per page. Three
  // of those rebuilt twice per tap for a 4px slide.
  const pageContent = useMemo(
    () =>
      showImage ? (
        <CardImage src={preview.image!} onError={handleFailed} />
      ) : (
        // The deck's own emoji / tag, rendered exactly as its DeckCard does
        // (see CardTagLabel) — a TAG steps down to fit the page, which is
        // 130px (compact) / 202px (expanded) wide against a deck tile's 180 /
        // 225, and only its top strip shows above the body panel.
        <CardTagLabel
          label={preview.emoji || DEFAULT_PREVIEW_EMOJI}
          color={preview.color}
          glyphSize={28}
          tagSize={size === 'expanded' ? 20 : 14}
        />
      ),
    [showImage, preview.image, preview.emoji, preview.color, size, handleFailed],
  );
  // EXPANDED emoji pages: the Figma revision (5729:10434) places the emoji
  // block exactly 30px below the page's top — not the old pt-4 peek. × the
  // responsive scale like the rest of the expanded card's px-fixed
  // geometry. Compact keeps the fixed pt-4.
  const emojiPad =
    !showImage && size === 'expanded'
      ? { paddingTop: Math.round(30 * scale) }
      : undefined;
  // No-image page tinted with the deck's identity colour — the same 30%/60%
  // `tintHex` pair as DeckCard's emoji tile. Inline style: arbitrary runtime hex.
  const tint =
    !showImage && preview.color
      ? {
          backgroundColor: tintHex(preview.color, 0.3, isDark),
          borderColor: tintHex(preview.color, 0.6, isDark),
        }
      : undefined;
  const style = {
    left: `${insetX}%`,
    right: `${insetX}%`,
    top: `${top}%`,
    ...tint,
  } as const;
  // NativeWind contract: translate utilities set --tw-* variables, and a
  // component must set them from its FIRST render — starting later flips
  // css-interop's upgrade state mid-life (dev warning + remount risk). So the
  // un-lifted state renders an explicit translate-y-0 instead of no class.
  const liftClasses = cn(
    PAGE_LIFT_TRANSITION,
    PAGE_LIFT_HOVER[liftDepth],
    lifted ? PAGE_LIFT_FORCED[liftDepth] : 'translate-y-0',
  );
  if (preview.folderColor) {
    // Subfolder page — the DS preview folder (`FolderPreviewShape`, Figma
    // 5686:88589/88907): −300 fill + a −500 accent stroke, so a subfolder
    // sharing the parent folder's colour still separates from the back.
    // Dimming mixes black into the identity colour
    // BEFORE the shape derives its fill/stroke pair, instead of a rect
    // overlay that would spill outside the tab's notch. Shadow-Secondary
    // (0/2/8 @6%) rides a web drop-shadow FILTER so it hugs the tab notch
    // (box-shadow would halo the transparent corner); native mirrors it
    // with the iOS alpha-derived layer shadow.
    const dimmed =
      liftDepth > 0
        ? mixHex(preview.folderColor, 'black', PAGE_DIM_MIX[liftDepth])
        : preview.folderColor;
    return (
      <View
        className={cn('absolute bottom-0', liftClasses)}
        webProps={{ style: { ...style, filter: FOLDER_PREVIEW_WEB_SHADOW } }}
        nativeProps={{ style: [style, FOLDER_PREVIEW_NATIVE_SHADOW] }}>
        <FolderPreviewShape color={dimmed} size={size} />
      </View>
    );
  }
  return (
    <View
      // No-media page: the white sheet (`bg-secondary-50`, Figma 3374:8908)
      // tinted with the deck's identity colour when it has one. The emoji
      // sits near the top (pt-4) because only that strip shows above the
      // body panel; an IMAGE page is covered edge-to-edge (object-cover), or
      // covers with non-white backgrounds show a white banner along the
      // page's top. Depth comes from the page shadow (`card-page`, Figma
      // 3374:9911 — native via PAGE_NATIVE_SHADOW) plus the graded dim.
      className={cn(
        'absolute bottom-0 items-center overflow-hidden rounded-t-[13px] border border-secondary-300 bg-secondary-50 web:shadow-card-page',
        // Dark: a page sits ON the folder, so it is lifted, never the -50 hole.
        DARK_LIFTED,
        // Side gutter so a tag ends before the page edge instead of on it.
        !showImage && 'px-2',
        !showImage && size !== 'expanded' && 'pt-4',
        liftClasses,
      )}
      webProps={{ style: { ...style, ...emojiPad } }}
      nativeProps={{ style: [style, emojiPad, PAGE_NATIVE_SHADOW] }}>
      {pageContent}
      {/* Back pages dim progressively so the front reads as the top of the
          stack — 5% one back, 10% two back (Figma). */}
      {liftDepth > 0 ? (
        <View className={cn('absolute inset-0', PAGE_DIM[liftDepth])} />
      ) : null}
    </View>
  );
}

/**
 * The grid cover for FolderCard / StudyGroupCard — a colour-filled folder
 * (tab top-left, `FolderShape`) with up to 3 previews stacked inside, their
 * tops peeking. Mirrors Figma's folder grid (3294:7246): the stack size
 * matches the item count (max 3); a deck preview shows that deck's cover
 * image, falls back to a plain white page carrying the deck's emoji
 * (3374:8908), then to the default 📝 page; a subfolder preview
 * (`folderColor`) is a mini colour folder. An empty folder shows just the
 * folder shape. Callers pick/order previews via `selectFolderPreviews`.
 */
export function FolderTile({
  color,
  previews,
  lifted = false,
  size = 'compact',
  dropPreview,
  overlay,
}: {
  color: string;
  previews?: FolderPreview[];
  /** Force the hover preview-lift (press / pen hover) — never a drop target. */
  lifted?: boolean;
  /** Grid subvariant — picks the folder shape drawn at the right aspect. */
  size?: 'compact' | 'expanded';
  /**
   * The item currently DRAGGED OVER this folder (web dnd). On an empty folder
   * it renders as a half-opacity ghost page — the hint that dropping will put
   * that item inside. Deliberately NOT derived from `lifted`: `lifted` also
   * fires on press-hold, where a ghost would be wrong.
   */
  dropPreview?: FolderPreview;
  /**
   * Painted over the folder back but UNDER the preview pages (the SG avatar
   * facepile) — a slot rather than a sibling because native paints strictly
   * in sibling order, so an after-the-tile overlay could never sit below the
   * pages there.
   */
  overlay?: ReactNode;
}) {
  const items = (previews ?? []).slice(0, 3);
  const scale = useResponsiveScale();
  const layoutKey = size === 'expanded' ? 'expanded' : 'compact';
  const layout = FOLDER_PAGE_LAYOUTS[layoutKey][items.length];
  // Drop hint: an EMPTY folder lit up as a drop target has nothing to lift, so
  // the drag would read as "nothing happens here" — the dragged item's own
  // page slides in as a ghost instead.
  const ghost = items.length === 0 ? dropPreview : undefined;
  return (
    <View className="relative w-full flex-1 overflow-hidden">
      {/* Colored folder back with the top-left tab. The tile now spans the
          FULL card on both sizes (so the pages can reach the card bottom),
          so the expanded back pins its own fixed Figma height (195 − the
          4px top inset) instead of filling to the bottom. Compact keeps
          bottom-0 — its back reaches the card bottom by design. */}
      <View
        className={cn(
          'absolute left-1 right-1 top-1',
          size === 'expanded' ? undefined : 'bottom-0',
        )}
        webProps={
          size === 'expanded' ? { style: { height: Math.round(191 * scale) } } : undefined
        }
        nativeProps={
          size === 'expanded' ? { style: { height: Math.round(191 * scale) } } : undefined
        }>
        <FolderShape color={color} size={size} />
      </View>
      {/* Overlay slot (SG facepile) — above the folder back, below the
          pages: the previews must always read as the frontmost stack. */}
      {overlay}
      {ghost ? (
        <View className="absolute inset-0 opacity-50">
          <PreviewPage
            preview={ghost}
            insetX={FOLDER_PAGE_LAYOUTS[layoutKey][1][0].insetX}
            top={FOLDER_PAGE_LAYOUTS[layoutKey][1][0].top}
            liftDepth={0}
            lifted
            size={size}
          />
        </View>
      ) : null}
      {/* `LAYOUTS` runs back → front, but the FIRST deck should be the one on
          top of the stack — so the last layout slot takes `items[0]`. */}
      {layout?.map((pos, i) => (
        <PreviewPage
          key={i}
          preview={items[layout.length - 1 - i]}
          insetX={pos.insetX}
          top={pos.top}
          liftDepth={layout.length - 1 - i}
          lifted={lifted}
          size={size}
        />
      ))}
    </View>
  );
}
